import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Pagination } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ViewListIcon from '@mui/icons-material/ViewList';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import moment from 'moment';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import {
  ExportHubAPI,
  ExportProduct,
  SavedView,
  ProductFacetOptions,
  BulkAction,
  BulkApplyResult,
} from 'api/ExportHubAPI';
import { JurisdictionPill, StatusPill, TierBadge } from './productPresets';
import ProductFilterBar from './ProductFilterBar';
import BulkApplyModal from './BulkApplyModal';
import SendPackageDialog from './SendPackageDialog';
import HierarchyView from './HierarchyView';
import ClassificationRunControl from './ClassificationRunControl';
import DeleteProductModal from './DeleteProductModal';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  ProductFilters,
  EMPTY_FILTERS,
  DEFAULT_COLUMNS,
  SavedViewState,
  parseFilters,
  applyFiltersToParams,
  parseColumns,
  applyColumnsToParams,
  filtersToApiParams,
  countActiveFilters,
} from './productFilters';

interface Props {
  databaseId: string;
  groupId: string | undefined;
  onOpenProduct: (_product: ExportProduct) => void;
  onRequestAdd: () => void;
  onRequestImport: () => void;
  reloadKey?: number;
}

const PAGE_SIZE = 25;

interface ColumnRender {
  label: string;
  sortKey?: string;
  render: (_p: ExportProduct) => ReactNode;
}

const COLUMN_RENDER: Record<string, ColumnRender> = {
  name: {
    label: 'Product',
    sortKey: 'name',
    render: (p) => (
      <div className='eh-product-name-cell'>
        <span className='eh-product-name'>{p.name}</span>
        <span className='eh-product-sub'>
          {p.internal_sku ? `SKU ${p.internal_sku}` : 'No SKU'}
          <TierBadge value={p.tier} />
        </span>
      </div>
    ),
  },
  hts_code: {
    label: 'HTS Code',
    sortKey: 'hts_code',
    render: (p) => p.hts_code || '—',
  },
  country_of_origin: {
    label: 'Origin',
    sortKey: 'country_of_origin',
    render: (p) => p.country_of_origin || '—',
  },
  compliance_status: {
    label: 'Compliance',
    sortKey: 'compliance_status',
    render: (p) => <StatusPill value={p.compliance_status} />,
  },
  jurisdiction: {
    label: 'Jurisdiction',
    sortKey: 'jurisdiction',
    render: (p) => <JurisdictionPill value={p.jurisdiction} />,
  },
  manufacturer: {
    label: 'Manufacturer',
    render: (p) => p.manufacturer || '—',
  },
  destinations: {
    label: 'Destinations',
    render: (p) =>
      p.intended_destination_countries.length
        ? p.intended_destination_countries.join(', ')
        : '—',
  },
  licenses: {
    label: 'Licenses',
    render: (p) => p.active_license_count,
  },
  last_screened_at: {
    label: 'Last Screened',
    sortKey: 'last_screened_at',
    render: (p) =>
      p.last_screened_at
        ? moment(p.last_screened_at).format('MM/DD/YYYY')
        : '—',
  },
  last_updated: {
    label: 'Updated',
    sortKey: 'last_updated',
    render: (p) => moment(p.last_updated).format('MM/DD/YYYY'),
  },
};

const ProductsPanel: React.FC<Props> = ({
  databaseId,
  groupId,
  onOpenProduct,
  onRequestAdd,
  onRequestImport,
  reloadKey,
}) => {
  const location = useLocation();
  const history = useHistory();
  const scope = `db:${databaseId}`;

  // URL-derived state (single source of truth for filters/columns/active view).
  const filters = useMemo(
    () => parseFilters(new URLSearchParams(location.search)),
    [location.search],
  );
  const visibleColumns = useMemo(
    () => parseColumns(new URLSearchParams(location.search)),
    [location.search],
  );
  const activeViewId = useMemo(
    () => new URLSearchParams(location.search).get('view'),
    [location.search],
  );
  const isHierarchy = useMemo(
    () => new URLSearchParams(location.search).get('layout') === 'hierarchy',
    [location.search],
  );

  const [products, setProducts] = useState<ExportProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [facets, setFacets] = useState<ProductFacetOptions>({
    origins: [],
    destinations: [],
    htsChapters: [],
    total: 0,
  });
  const [savedViews, setSavedViews] = useState<SavedView[]>([]);
  const [savingView, setSavingView] = useState(false);

  // Bulk product export risk (commodity) screening run.
  const [productScreening, setProductScreening] = useState(false);
  // % complete while the async Automations run processes (0–100), or null.
  const [productScreenPct, setProductScreenPct] = useState<number | null>(null);

  // Bulk selection.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // The row whose delete was clicked; null keeps the confirmation closed.
  const [deleteTarget, setDeleteTarget] = useState<ExportProduct | null>(null);
  const [applyToFiltered, setApplyToFiltered] = useState(false);
  const [bulkAction, setBulkAction] = useState<BulkAction | null>(null);
  const [sendOpen, setSendOpen] = useState(false);

  const filtersKey = JSON.stringify(filters);

  // URL writer that preserves unrelated params.
  const updateUrl = useCallback(
    (mutate: (_sp: URLSearchParams) => void) => {
      const sp = new URLSearchParams(location.search);
      mutate(sp);
      history.replace({ pathname: location.pathname, search: sp.toString() });
    },
    [location.search, location.pathname, history],
  );

  const setFilters = (next: ProductFilters) =>
    updateUrl((sp) => {
      applyFiltersToParams(sp, next);
      sp.delete('view');
    });

  const setColumns = (next: string[]) =>
    updateUrl((sp) => {
      applyColumnsToParams(sp, next);
      sp.delete('view');
    });

  const applyView = (v: SavedView) => {
    const state = (v.facets || {}) as Partial<SavedViewState>;
    updateUrl((sp) => {
      applyFiltersToParams(sp, { ...EMPTY_FILTERS, ...(state.filters || {}) });
      applyColumnsToParams(
        sp,
        state.columns && state.columns.length ? state.columns : DEFAULT_COLUMNS,
      );
      sp.set('view', v.id);
    });
  };

  const setLayout = (mode: 'flat' | 'hierarchy') =>
    updateUrl((sp) => {
      if (mode === 'hierarchy') sp.set('layout', 'hierarchy');
      else sp.delete('layout');
    });

  // Reset to page 1 whenever the filter set changes.
  useEffect(() => {
    setPage(1);
  }, [filtersKey]);

  // Clear bulk selection when the result set changes underneath it.
  useEffect(() => {
    setSelectedIds(new Set());
    setApplyToFiltered(false);
  }, [filtersKey, debouncedSearch]);

  // Debounce search.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    if (!groupId) {
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const res = await ExportHubAPI.getProducts(databaseId, groupId, {
        page: page,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
        sortBy: sortBy,
        sortOrder: sortOrder,
        ...filtersToApiParams(filters),
      });
      setProducts(res.products);
      setTotalPages(res.pagination.totalPages || 1);
      setTotalItems(res.pagination.totalItems || 0);
    } catch (err) {
      console.error('Failed to load products:', err);
      setError(true);
      enqueueSnackbar('Failed to load products.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
    // filtersKey (a stable string) stands in for the filters object so this
    // callback is recreated whenever the active filter set changes.
  }, [databaseId, groupId, page, debouncedSearch, sortBy, sortOrder, filtersKey]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  // Bulk product export risk screen: commodity screen every HTS-bearing product
  // (no AI — the risk response stays opt-in per product). Poll, then refresh.
  const runProductScreening = useCallback(async () => {
    if (!groupId || productScreening) return;
    setProductScreening(true);
    setProductScreenPct(0);
    try {
      const run = await ExportHubAPI.startProductScreening(databaseId, groupId);
      if (!run) throw new Error('No run started');
      if (run.products_total === 0) {
        enqueueSnackbar('No products with an HTS code to screen.', {
          variant: 'info',
        });
        return;
      }
      enqueueSnackbar(
        `Screening ${run.products_total.toLocaleString()} products — this runs in the background; you'll be notified when it finishes.`,
        { variant: 'info' },
      );
      let cur: typeof run | null = run;
      // Poll the run header (bumped by the Automations workers) for progress.
      // eslint-disable-next-line no-await-in-loop
      while (cur && cur.status === 'processing') {
        const pct =
          cur.products_total > 0
            ? Math.min(
                99,
                Math.floor((100 * cur.products_processed) / cur.products_total),
              )
            : 0;
        setProductScreenPct(pct);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 2500));
        // eslint-disable-next-line no-await-in-loop
        cur = await ExportHubAPI.getScreeningRun(run.id, groupId);
      }
      setProductScreenPct(100);
      const failed = cur?.status === 'failed';
      enqueueSnackbar(
        failed
          ? 'Product screening failed. Please try again.'
          : `Product screening complete — ${cur?.products_flagged ?? 0} of ${
              cur?.products_processed ?? 0
            } with commodity hits.`,
        { variant: failed ? 'error' : 'success' },
      );
      load();
    } catch (err) {
      console.error('Product screening failed:', err);
      enqueueSnackbar('Product screening failed to run.', { variant: 'error' });
    } finally {
      setProductScreening(false);
      setProductScreenPct(null);
    }
  }, [databaseId, groupId, productScreening, load]);

  const refreshFacets = useCallback(async () => {
    if (!groupId) return;
    try {
      setFacets(await ExportHubAPI.getProductFacets(databaseId, groupId));
    } catch (err) {
      console.error('Failed to load facets:', err);
    }
  }, [databaseId, groupId]);

  const refreshViews = useCallback(async () => {
    if (!groupId) return;
    try {
      setSavedViews(await ExportHubAPI.getSavedViews(groupId, scope));
    } catch (err) {
      console.error('Failed to load saved views:', err);
    }
  }, [groupId, scope]);

  useEffect(() => {
    refreshFacets();
  }, [refreshFacets, reloadKey]);

  useEffect(() => {
    refreshViews();
  }, [refreshViews]);

  // ----- Saved view handlers -----
  const handleSaveView = async (name: string) => {
    if (!groupId) return;
    setSavingView(true);
    try {
      const view = await ExportHubAPI.createSavedView({
        scope: scope,
        name: name,
        facets: { filters: filters, columns: visibleColumns },
        group_id: groupId,
      });
      await refreshViews();
      updateUrl((sp) => sp.set('view', view.id));
      enqueueSnackbar('View saved.', { variant: 'success' });
    } catch (err) {
      console.error('Failed to save view:', err);
      enqueueSnackbar('Failed to save view.', { variant: 'error' });
    } finally {
      setSavingView(false);
    }
  };

  const handlePinView = async (v: SavedView) => {
    if (!groupId) return;
    try {
      await ExportHubAPI.updateSavedView(v.id, groupId, {
        is_pinned: !v.is_pinned,
      });
      await refreshViews();
    } catch (err) {
      enqueueSnackbar('Failed to update view.', { variant: 'error' });
    }
  };

  const handleRenameView = async (v: SavedView, name: string) => {
    if (!groupId || !name.trim()) return;
    try {
      await ExportHubAPI.updateSavedView(v.id, groupId, { name: name.trim() });
      await refreshViews();
    } catch (err) {
      enqueueSnackbar('Failed to rename view.', { variant: 'error' });
    }
  };

  const handleDeleteView = async (v: SavedView) => {
    if (!groupId) return;
    try {
      await ExportHubAPI.deleteSavedView(v.id, groupId);
      await refreshViews();
      if (activeViewId === v.id) {
        updateUrl((sp) => sp.delete('view'));
      }
      enqueueSnackbar('View deleted.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to delete view.', { variant: 'error' });
    }
  };

  const handleSort = (sortKey: string) => {
    if (sortBy === sortKey) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(sortKey);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const sortIcon = (key: string) => {
    if (sortBy !== key) return null;
    return sortOrder === 'asc' ? (
      <ArrowUpwardIcon sx={{ fontSize: 14 }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 14 }} />
    );
  };

  const activeCount = countActiveFilters(filters);

  // ----- Bulk selection -----
  const toggleRow = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allVisibleSelected =
    products.length > 0 && products.every((p) => selectedIds.has(p.id));
  const toggleAll = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (products.every((p) => prev.has(p.id))) {
        products.forEach((p) => next.delete(p.id));
      } else {
        products.forEach((p) => next.add(p.id));
      }
      return next;
    });

  const bulkScope = applyToFiltered
    ? { search: debouncedSearch || undefined, filters: filters }
    : { ids: Array.from(selectedIds) };
  const bulkCount = applyToFiltered ? totalItems : selectedIds.size;

  const undoField = async (
    field: string,
    previous: Array<{ id: string; value: unknown }>,
  ) => {
    if (!groupId) return;
    try {
      await ExportHubAPI.bulkApply(databaseId, {
        group_id: groupId,
        scope: {},
        action: 'restore_field',
        params: { field: field, items: previous },
      });
      load();
      refreshFacets();
      enqueueSnackbar('Reverted.', { variant: 'info' });
    } catch (err) {
      enqueueSnackbar('Undo failed.', { variant: 'error' });
    }
  };

  const onBulkApplied = (result: BulkApplyResult) => {
    setBulkAction(null);
    setSelectedIds(new Set());
    setApplyToFiltered(false);
    load();
    refreshFacets();
    const msg = `Updated ${result.affected.toLocaleString()} product${
      result.affected === 1 ? '' : 's'
    }.`;
    if (result.field && result.previous && result.previous.length) {
      const field = result.field;
      const previous = result.previous;
      enqueueSnackbar(msg, {
        variant: 'success',
        autoHideDuration: 10000,
        action: (snackId) => (
          <button
            className='eh-snack-undo'
            type='button'
            onClick={() => {
              undoField(field, previous);
              closeSnackbar(snackId);
            }}
          >
            Undo
          </button>
        ),
      });
    } else {
      enqueueSnackbar(msg, { variant: 'success' });
    }
  };

  const toolbar = (
    <div className='eh-products-toolbar'>
      <div className='data-search-wrapper'>
        <SearchIcon className='data-search-icon' style={{ fontSize: 18 }} />
        <input
          type='text'
          className='data-search-input'
          placeholder='Search by name, SKU, or HTS'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className='eh-toolbar-right'>
        <ClassificationRunControl
          databaseId={databaseId}
          groupId={groupId}
          onComplete={load}
        />
        <button
          type='button'
          className='btn-outline'
          onClick={runProductScreening}
          disabled={productScreening}
          title='Run the commodity export risk screen over every product with an HTS code'
        >
          <TravelExploreOutlinedIcon
            className={productScreening ? 'eh-spin' : undefined}
            style={{ fontSize: 16, marginRight: 6, verticalAlign: 'middle' }}
          />
          {productScreening
            ? productScreenPct !== null
              ? `Screening… ${productScreenPct}%`
              : 'Screening…'
            : 'Screen Products'}
        </button>
        <div className='eh-view-toggle'>
          <button
            type='button'
            className={`eh-view-toggle-btn ${!isHierarchy ? 'active' : ''}`}
            onClick={() => setLayout('flat')}
            title='Flat view'
          >
            <ViewListIcon style={{ fontSize: 18 }} />
          </button>
          <button
            type='button'
            className={`eh-view-toggle-btn ${isHierarchy ? 'active' : ''}`}
            onClick={() => setLayout('hierarchy')}
            title='Hierarchy view'
          >
            <AccountTreeOutlinedIcon style={{ fontSize: 18 }} />
          </button>
        </div>
      </div>
    </div>
  );

  const filterBar = (
    <ProductFilterBar
      filters={filters}
      onFiltersChange={setFilters}
      facetOptions={facets}
      visibleColumns={visibleColumns}
      onColumnsChange={setColumns}
      totalCount={facets.total}
      filteredCount={totalItems}
      savedViews={savedViews}
      activeViewId={activeViewId}
      onApplyView={applyView}
      onSaveView={handleSaveView}
      onPinView={handlePinView}
      onRenameView={handleRenameView}
      onDeleteView={handleDeleteView}
      savingView={savingView}
    />
  );

  if (isHierarchy) {
    return (
      <div className='export-hub-panel'>
        {toolbar}
        <HierarchyView
          databaseId={databaseId}
          groupId={groupId}
          onOpenProduct={onOpenProduct}
          reloadKey={reloadKey}
        />
      </div>
    );
  }

  if (loading && products.length === 0) {
    return (
      <div className='export-hub-panel'>
        {toolbar}
        <div className='eh-table-skeleton'>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className='eh-skel eh-skel-row' />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='export-hub-panel'>
        {toolbar}
        <div className='export-hub-empty'>
          <div className='export-hub-empty-title'>
            Couldn&rsquo;t load products
          </div>
          <button
            className='btn-outline'
            style={{ marginTop: 12 }}
            onClick={load}
            type='button'
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Truly empty (no products at all) → first-run empty state, no filter bar.
  if (products.length === 0 && activeCount === 0 && !debouncedSearch) {
    return (
      <div className='export-hub-panel'>
        <div className='export-hub-empty'>
          <div className='export-hub-empty-title'>No products yet</div>
          <div className='export-hub-empty-sub'>
            Add your first product to start tracking export compliance,
            screenings, and licenses.
          </div>
          <div
            style={{
              marginTop: 16,
              display: 'flex',
              gap: 10,
              justifyContent: 'center',
            }}
          >
            <button
              className='btn-outline'
              onClick={onRequestImport}
              type='button'
            >
              <FileUploadOutlinedIcon style={{ fontSize: 18 }} /> Import CSV/XLSX
            </button>
            <button className='btn-primary' onClick={onRequestAdd} type='button'>
              <AddIcon style={{ fontSize: 18 }} /> Add Product
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='export-hub-panel'>
      {toolbar}
      {filterBar}
      {(selectedIds.size > 0 || applyToFiltered) && (
        <div className='eh-bulk-bar'>
          <span className='eh-bulk-count'>
            {bulkCount.toLocaleString()} selected
          </span>
          <div className='eh-bulk-actions'>
            <button
              className='eh-bulk-act'
              type='button'
              onClick={() => setBulkAction('set_jurisdiction')}
            >
              Set Jurisdiction
            </button>
            <button
              className='eh-bulk-act'
              type='button'
              onClick={() => setBulkAction('set_country_of_origin')}
            >
              Set Origin
            </button>
            <button
              className='eh-bulk-act'
              type='button'
              onClick={() => setBulkAction('set_compliance_status')}
            >
              Set Status
            </button>
            <button
              className='eh-bulk-act'
              type='button'
              onClick={() => setBulkAction('link_license')}
            >
              Link License
            </button>
            <button
              className='eh-bulk-act'
              type='button'
              onClick={() => setSendOpen(true)}
            >
              Send Package
            </button>
          </div>
          {totalItems > selectedIds.size && (
            <label className='eh-bulk-allfiltered'>
              <input
                type='checkbox'
                checked={applyToFiltered}
                onChange={(e) => setApplyToFiltered(e.target.checked)}
              />
              Apply to all {totalItems.toLocaleString()} matching
            </label>
          )}
          <button
            className='eh-bulk-clear'
            type='button'
            onClick={() => {
              setSelectedIds(new Set());
              setApplyToFiltered(false);
            }}
          >
            Clear
          </button>
        </div>
      )}
      <div className='runs-content-container'>
        <table className='runs-table'>
          <thead>
            <tr>
              <th className='eh-check-col'>
                <input
                  type='checkbox'
                  checked={allVisibleSelected}
                  onChange={toggleAll}
                  aria-label='Select all'
                />
              </th>
              {visibleColumns.map((key) => {
                const col = COLUMN_RENDER[key];
                if (!col) return null;
                const sk = col.sortKey;
                return (
                  <th
                    key={key}
                    onClick={sk ? () => handleSort(sk) : undefined}
                    style={
                      sk ? { cursor: 'pointer', userSelect: 'none' } : undefined
                    }
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      {col.label}
                      {sk && sortIcon(sk)}
                    </div>
                  </th>
                );
              })}
              <th className='eh-row-action-col' aria-label='Actions' />
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr
                key={p.id}
                className={`hoverable-row ${
                  selectedIds.has(p.id) ? 'eh-row-sel' : ''
                }`}
                style={{ cursor: 'pointer' }}
                onClick={() => onOpenProduct(p)}
              >
                <td
                  className='eh-check-col'
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type='checkbox'
                    checked={selectedIds.has(p.id)}
                    onChange={() => toggleRow(p.id)}
                    aria-label='Select product'
                  />
                </td>
                {visibleColumns.map((key) => {
                  const col = COLUMN_RENDER[key];
                  if (!col) return null;
                  return <td key={key}>{col.render(p)}</td>;
                })}
                <td
                  className='eh-row-action-col'
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type='button'
                    className='eh-row-delete'
                    title={`Delete ${p.name}`}
                    aria-label={`Delete ${p.name}`}
                    onClick={() => setDeleteTarget(p)}
                  >
                    <DeleteOutlineIcon style={{ fontSize: 16 }} />
                  </button>
                </td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr>
                <td
                  colSpan={visibleColumns.length + 2}
                  className='eh-no-match'
                >
                  No products match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div
            className='hb-pagination'
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 24px',
              borderTop: '1px solid var(--es-soft-border)',
            }}
          >
            <span
              className='pagination-text'
              style={{ color: 'var(--text-secondary)', fontSize: '14px' }}
            >
              {totalItems.toLocaleString()} product
              {totalItems === 1 ? '' : 's'}
            </span>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_e, v) => setPage(v)}
              size='large'
              shape='rounded'
              variant='text'
            />
          </div>
        )}
      </div>

      <BulkApplyModal
        open={bulkAction !== null}
        action={bulkAction}
        databaseId={databaseId}
        groupId={groupId}
        scope={bulkScope}
        count={bulkCount}
        onClose={() => setBulkAction(null)}
        onApplied={onBulkApplied}
      />
      <SendPackageDialog
        open={sendOpen}
        subjectLabel={`${bulkCount} product${bulkCount === 1 ? '' : 's'}`}
        onClose={() => setSendOpen(false)}
      />
      <DeleteProductModal
        open={!!deleteTarget}
        product={deleteTarget}
        groupId={groupId}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => {
          setDeleteTarget(null);
          // Drop it from any pending bulk selection before reloading.
          setSelectedIds((prev) => {
            const next = new Set(prev);
            if (deleteTarget) next.delete(deleteTarget.id);
            return next;
          });
          load();
        }}
      />
    </div>
  );
};

export default ProductsPanel;
